import React, { Fragment, useMemo } from "react";
import { Row, Table } from "reactstrap";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import Select from "react-select";
import { FadeLoader } from "react-spinners";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import { Link } from "react-router-dom";

const AssignmentDataTable = ({
  data,
  loading,
  totalrows,
  selectedSortData,
  setSelectedSortData,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  searchString,
  setSearchString,
  selectedStatus,
  setSelectedStatus,
  selectedCreator,
  setSelectedCreator,
  creatorOptions = [],
}) => {
  // Columns setup with serial number and formatted data
  const columns = useMemo(
    () => [
      {
        header: "#",
        id: "serial",
        cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
      },
    {
  header: "Title",
  accessorKey: "title",
  cell: ({ row }) => (
    <Link
      to={`/assignments/${row.original._id}`}
      className="text-primary"
      style={{ textDecoration: "none" }}
    >
      {row.original.title}
    </Link>
  ),
},

      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }) => (
          <span title={row.original?.description}>
            {row.original?.description?.length > 40
              ? row.original.description.slice(0, 40) + "..."
              : row.original.description || "-"}
          </span>
        ),
      },
      {
        header: "Deadline",
        accessorKey: "deadline",
        cell: ({ row }) => formatDateTimeToAmPm(row.original?.deadline),
      },
      { header: "Total Marks", accessorKey: "totalMarks" },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <span className={row.original.status === "Active" ? "text-success" : "text-danger"}>
            {row.original.status}
          </span>
        ),
      },
      {
        header: "Created By",
        accessorKey: "createdBy.name",
        cell: ({ row }) => row.original?.createdBy?.name || "-",
      },
    ],
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const totalPages = Math.ceil(totalrows / pageSize);
  const handlePageChange = (newPageIndex) => setPageIndex(newPageIndex);

  const statusOptions = [
    { label: "All", value: "" },
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  return (
    <Fragment>
      {/* 🔹 Filters + Search */}
      <Row className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap w-100">
          {/* Left filters */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/*  Filter by Status */}
            {/* <div style={{ width: "220px" }}>
              <Select
                placeholder="Filter by Status"
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={statusOptions}
                isClearable
              />
            </div> */}

            {/*  Filter by Creator */}
            <div style={{ width: "220px" }}>
              <Select
                placeholder="Filter by Creator"
                value={selectedCreator}
                onChange={setSelectedCreator}
                options={creatorOptions}
                isClearable
              />
            </div>
          </div>

          {/*  Right-aligned search bar */}
          <div style={{ width: "250px" }}>
            <DebouncedInput
              value={searchString ?? ""}
              onChange={(value) => setSearchString(String(value))}
              className="form-control"
              placeholder="Search assignments..."
            />
          </div>
        </div>
      </Row>

      {/* 🔹 Table */}
      <div className="table-responsive">
        <Table bordered hover>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                      {header.column.columnDef.accessorKey && (
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const field = header.column.columnDef.accessorKey;
                            if (!field) return;
                            if (!selectedSortData || selectedSortData.value !== field)
                              setSelectedSortData({ value: field, direction: "asc" });
                            else if (selectedSortData.direction === "asc")
                              setSelectedSortData({ value: field, direction: "desc" });
                            else setSelectedSortData({ value: field, direction: "asc" });
                          }}
                        >
                          {selectedSortData?.value === header.column.columnDef.accessorKey ? (
                            selectedSortData.direction === "asc" ? (
                              <i className="mdi mdi-arrow-up"></i>
                            ) : (
                              <i className="mdi mdi-arrow-down"></i>
                            )
                          ) : (
                            <i className="mdi mdi-swap-vertical"></i>
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
<td colSpan={columns.length} style={{ height: "120px" }}>

  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
    }}
  >
    <FadeLoader color="#00a895" size={40} />
  </div>
</td>
              </tr>
            ) : data?.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No Data Found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalrows > 0 && (
        <Pagination
          currentPage={pageIndex}
          totalPages={totalPages}
          totalItems={totalrows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          showInfo
        />
      )}
    </Fragment>
  );
};

export default AssignmentDataTable;
